import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import clamp from 'clamp';
import Ember from 'ember';
import { localCopy } from 'tracked-toolbox';

// eslint-disable-next-line no-undef
const { escapeExpression } = (<typeof Handlebars>(<unknown>Ember.Handlebars)).Utils;

interface LineClampSignature {
  Args: {
    /**
     * the maximum # of lines the text should be displayed on
     */
    lines: number;
    /**
     * the line-height of the lines of text
     */
    lineHeight?: string | number;
    /**
     * the content being "clamped"
     */
    text?: string;
    /**
     * string to append to truncated text (default: '…')
     */
    truncationChar?: string;
    /**
     * @param isTruncated a callback function executed after truncation
     */
    handleTruncate?: (isTruncated: boolean) => void;
    useNative?: boolean;
  };
  Element: HTMLDivElement;
}

export default class LineClamp extends Component<LineClampSignature> {
  @tracked clampedText?: string;
  @localCopy('args.truncationChar', '…') truncationChar!: LineClampSignature['Args']['truncationChar'];
  @localCopy('args.useNative', false) useNative!: LineClampSignature['Args']['useNative'];

  get style(): string {
    return `line-height: ${this.args.lineHeight};`;
  }

  get text(): string {
    return this.args.text ?? '';
  }

  constructor(properties: unknown, args: LineClampSignature['Args']) {
    super(properties, args);
  }

  @action
  clamp(element: HTMLElement): void {
    const { args, useNative, text, truncationChar } = this;
    const { lines } = args;

    // eslint-disable-next-line ember/no-runloop, ember/no-incorrect-calls-with-inline-anonymous-functions
    scheduleOnce('afterRender', this, () => {
      element.innerHTML = escapeExpression(text);

      const { clamped: clampedText } = clamp(element, {
        clamp: lines,
        truncationChar,
        useNativeClamp: !!useNative,
      });

      // Check for both clamped value and indication of native line-clamp
      if (clampedText || element.scrollHeight > element.clientHeight) {
        this.clampedText = clampedText;
        this.args.handleTruncate?.(true);
      } else {
        this.args.handleTruncate?.(false);
      }
    });
  }

  <template>
    <div style={{this.style}} ...attributes {{didInsert this.clamp}} {{didUpdate this.clamp this.text}}>
      {{if this.clampedText this.clampedText this.text}}
    </div>
  </template>
}
